import React from 'react';
import { Chip, Box, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { CheckCircle as SuccessIcon, Error as ErrorIcon, HourglassEmpty as RunningIcon } from '@material-ui/icons';
import distanceInWordsToNow from 'date-fns/distance_in_words_to_now';
import format from 'date-fns/format';

import type { ExecutionStatus, AnalyticsExecution } from '../types';
import { normalizeStatus, formatDuration } from '../utils';

const useStyles = makeStyles(theme => ({
  statusChip: {
    fontWeight: 500,
  },
  successChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  errorChip: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.dark,
  },
  runningChip: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.dark,
  },
  enabledChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.dark,
  },
  disabledChip: {
    backgroundColor: theme.palette.grey[300],
    color: theme.palette.grey[700],
  },
}));

interface StatusChipProps {
  status: ExecutionStatus;
}

/**
 * Displays execution status as a colored chip with icon.
 */
export const StatusChip: React.FC<StatusChipProps> = ({ status }) => {
  const classes = useStyles();

  const config = {
    success: { icon: <SuccessIcon fontSize="small" />, label: 'Success', className: classes.successChip },
    failed: { icon: <ErrorIcon fontSize="small" />, label: 'Failed', className: classes.errorChip },
    running: { icon: <RunningIcon fontSize="small" />, label: 'Running', className: classes.runningChip },
  };

  const normalized = normalizeStatus(status);
  const { icon, label, className } = config[normalized];

  return <Chip icon={icon} label={label} size="small" className={`${classes.statusChip} ${className}`} />;
};

interface EnabledChipProps {
  enabled: boolean;
}

/**
 * Displays enabled/paused status as a chip.
 */
export const EnabledChip: React.FC<EnabledChipProps> = ({ enabled }) => {
  const classes = useStyles();
  return (
    <Chip
      label={enabled ? 'Enabled' : 'Paused'}
      size="small"
      className={enabled ? classes.enabledChip : classes.disabledChip}
    />
  );
};

interface LastExecutionStatusProps {
  execution?: AnalyticsExecution;
}

/**
 * Compact last execution status indicator with tooltip showing details.
 */
export const LastExecutionStatus: React.FC<LastExecutionStatusProps> = ({ execution }) => {
  if (!execution) {
    return (
      <Typography variant="caption" color="textSecondary">
        Never run
      </Typography>
    );
  }

  const statusConfig = {
    success: { icon: <SuccessIcon style={{ fontSize: 14, color: '#4caf50' }} /> },
    failed: { icon: <ErrorIcon style={{ fontSize: 14, color: '#f44336' }} /> },
    running: { icon: <RunningIcon style={{ fontSize: 14, color: '#ff9800' }} /> },
  };

  const normalized = normalizeStatus(execution.status);
  const { icon } = statusConfig[normalized];
  const timeAgo = distanceInWordsToNow(new Date(execution.startedAt), { addSuffix: true });

  return (
    <Tooltip
      title={
        <>
          <div>Status: {execution.status}</div>
          <div>Started: {format(new Date(execution.startedAt), 'PPpp')}</div>
          {execution.durationMs && <div>Duration: {formatDuration(execution.durationMs)}</div>}
          {execution.errorMessage && <div style={{ color: '#f44336' }}>Error: {execution.errorMessage}</div>}
        </>
      }
    >
      <Box display="flex" alignItems="center" style={{ gap: 4 }}>
        {icon}
        <Typography variant="caption" color="textSecondary">
          {timeAgo}
        </Typography>
      </Box>
    </Tooltip>
  );
};
